import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { useSearch } from "@tanstack/react-router";
import Modal from "@/components/Modal";
import { usePrefetchAiAssist } from "@/queries/useAiAssist";
import { useTranslations } from "@/queries/useTranslations";
import { type IDispatch, type IState, defaultContentItemSelector, editContent, resetEdit } from "@/state";
import { CompactActionButton, ContentRow, IDHeading, Item } from "@/styles/stylings";
import { type ILanguage, LANGUAGES, REFERENCE_LANGUAGE } from "@/types";
import AiAssist from "@/features/editor/AiAssist";
import ContextEditor from "@/features/editor/ContextEditor";
import { buildCopyRowText } from "@/features/editor/copyRow";
import DefaultContentItem from "./DefaultContentItem";
import History from "@/features/editor/History";
import { activeLanguages } from "@/urlState";

interface IProps {
    id: string;
    version: string;
}

function DefaultContentRow({ id, version }: IProps) {
    const dispatch = useDispatch<IDispatch>();
    const { data: translations } = useTranslations(version);
    const texts = useSelector((state: IState) => defaultContentItemSelector(state, version, id, translations!));
    const search = useSearch({ from: "/$version" });
    const langs = activeLanguages(search);
    const [aiModalContent, setAiModalContent] = React.useState<{
        text: string;
        lang: ILanguage;
    } | null>(null);
    const [historyModalContent, setHistoryModalContent] = React.useState<ILanguage | null>(null);
    const referenceText = texts[REFERENCE_LANGUAGE];
    const context = translations?.contexts.defaultContent[id] ?? "";

    usePrefetchAiAssist({
        id,
        contentType: "defaultContent",
        languages: langs,
        text: referenceText,
        version,
    });

    const changeHandler = React.useCallback(
        (event: React.FormEvent<HTMLTextAreaElement>, lang: ILanguage) => {
            dispatch(
                editContent({
                    version,
                    lang,
                    id,
                    contentType: "defaultContent",
                    value: event.currentTarget.value,
                }),
            );
        },
        [dispatch, version, id],
    );

    const resetHandler = React.useCallback(
        (lang: ILanguage) => {
            if (!window.confirm("This will throw away your changes reset it back to the latest saved version.")) {
                return;
            }
            dispatch(resetEdit({ version, lang, id, contentType: "defaultContent" }));
        },
        [dispatch, version, id],
    );

    const closeAi = React.useCallback(() => {
        setAiModalContent(null);
    }, []);

    const closeHistory = React.useCallback(() => {
        setHistoryModalContent(null);
    }, []);

    const setAiModal = React.useCallback(
        (lang: ILanguage) => {
            setAiModalContent({ lang, text: referenceText });
        },
        [referenceText],
    );

    const setHistoryModal = React.useCallback((lang: ILanguage) => {
        setHistoryModalContent(lang);
    }, []);

    const copyRow = React.useCallback(() => {
        navigator.clipboard.writeText(buildCopyRowText(id, context, texts, langs));
    }, [context, id, texts, langs]);

    return (
        <Item>
            <IDHeading>
                <strong>ID:</strong> {id}
                <CompactActionButton type="button" onClick={copyRow}>
                    Copy row
                </CompactActionButton>
            </IDHeading>
            <ContextEditor version={version} id={id} contentType="defaultContent" context={context} />
            <ContentRow>
                {LANGUAGES.filter((lang) => langs.includes(lang)).map((lang) => (
                    <DefaultContentItem
                        key={lang}
                        lang={lang}
                        text={texts[lang]}
                        resetHandler={resetHandler}
                        changeHandler={changeHandler}
                        setAiModal={setAiModal}
                        setHistoryModal={setHistoryModal}
                        id={id}
                        version={version}
                    />
                ))}
            </ContentRow>
            <Modal isOpen={!!aiModalContent} onRequestClose={closeAi} contentLabel="AI Assist">
                <AiAssist
                    close={closeAi}
                    version={version}
                    id={id}
                    text={aiModalContent?.text ?? ""}
                    lang={aiModalContent?.lang ?? "en"}
                    contentType="defaultContent"
                />
            </Modal>
            <Modal isOpen={!!historyModalContent} onRequestClose={closeHistory} contentLabel="Translation history">
                <History
                    close={closeHistory}
                    version={version}
                    id={id}
                    lang={historyModalContent ?? "en"}
                    contentType="defaultContent"
                />
            </Modal>
        </Item>
    );
}

export default React.memo(DefaultContentRow);
